import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import {toast, ToastContainer } from "react-toastify";
import DepartmentService from "../../../../services/DepartmentService";
import EmployeeService from '../../../../services/EmployeeService';

const EmployeeFormModal = ({ show, handleClose ,customer,refreshEmployeeList}) => {
    const { register, handleSubmit, control, reset, formState: { errors } } = useForm();
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const onSubmit = async (data) => {
        const requestBody = {
            data: {
                ...data,
                customer_id : customer.value,
                is_contact_person: data.is_contact_person ? 1 : 0,
            },
        };

        try {
            await EmployeeService.create(requestBody);
            toast.success('Employee created successfully!');
            reset();
            refreshEmployeeList();
            handleClose();
        } catch (error) {
            console.log('Error in creating new employee:', [error]);
            toast.error('An error occurred while creating the employee.');
        }
    };

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await DepartmentService.listAll();
                if (response.status === 200 && response.data.success) {
                    setDepartments(response.data.data || []);
                } else {
                    console.error('Error fetching data:', response);
                }
            } catch (error) {
                console.error('Error:', error);
            } 
        };

        fetchDepartments();
    }, []);

    // Handle department change
    const handleDepartmentChange = (event) => {
        const selectedDepartmentId = event.target.value;
        const selectedDepartment = departments.find(department => department.id === parseInt(selectedDepartmentId));
        setDesignations(selectedDepartment ? selectedDepartment.designations : []);
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton>
                <Modal.Title>{customer && customer.label ? customer.label : "Employee Form"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        {/* First Name */}
                        <Form.Group className="mb-3">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter First Name"
                                {...register('first_name', { required: 'First Name is required' })}
                                isInvalid={!!errors.firstName}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.firstName?.message}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {/* Last Name */}
                        <Form.Group className="mb-3">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Last Name"
                                {...register('last_name', { required: 'Last Name is required' })}
                                isInvalid={!!errors.lastName}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.lastName?.message}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {/* Email Address */}
                        <Form.Group className="mb-3">
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter Email Address"
                                {...register('email', {
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                        message: 'Invalid email address',
                                    },
                                })}
                                isInvalid={!!errors.email}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.email?.message}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {/* Phone Number */}
                        <Form.Group className="mb-3">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                                type="tel"
                                maxLength={10}
                                placeholder="Enter Phone Number"
                                {...register('phone', {
                                    required: 'Phone number is required',
                                    pattern: {
                                        value: /^[0-9]{10}$/,
                                        message: 'Phone number must be 10 digits',
                                    },
                                })}
                                isInvalid={!!errors.phone}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.phone?.message}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {/* Gender Radio Buttons */}
                        <Form.Group className="mb-3">
                            <Form.Label>Gender</Form.Label>
                            <div>
                                <Form.Check
                                    type="radio"
                                    id="female"
                                    label="Female"
                                    value="female"
                                    {...register('gender', { required: 'Please select a gender' })}
                                    isInvalid={!!errors.gender}
                                />
                                <Form.Check
                                    type="radio"
                                    id="male"
                                    label="Male"
                                    value="male"
                                    {...register('gender', { required: 'Please select a gender' })}
                                />
                                <Form.Check
                                    type="radio"
                                    id="other"
                                    label="Other"
                                    value="other"
                                    {...register('gender', { required: 'Please select a gender' })}
                                />
                            </div>
                            {errors.gender && (
                                <Form.Text className="text-danger">{errors.gender.message}</Form.Text>
                            )}
                        </Form.Group>
                        {/* Department Dropdown */}
                        <Form.Group className="mb-3">
                            <Form.Label>Department</Form.Label>
                            <Controller
                                name="department_id"
                                control={control}
                                rules={{ required: 'Please select a department' }}
                                render={({ field }) => (
                                    <Form.Select {...field} isInvalid={!!errors.department} onChange={(e) => {
                                        field.onChange(e);
                                        handleDepartmentChange(e);
                                    }}>
                                        <option value="">Select Department</option>
                                        {departments.map((department) => (
                                            <option key={department.id} value={department.id}>
                                                {department.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.department?.message}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {/* Designation Dropdown */}
                        <Form.Group className="mb-3">
                            <Form.Label>Designation</Form.Label>
                            <Controller
                                name="designation_id"
                                control={control}
                                rules={{ required: 'Please select a designation' }}
                                render={({ field }) => (
                                    <Form.Select {...field} isInvalid={!!errors.designation}>
                                        <option value="">Select Designation</option>
                                        {designations.map((designation) => (
                                            <option key={designation.id} value={designation.id}>
                                                {designation.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.designation?.message}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {/* Is Contact Person Checkbox */}
                        <Form.Group className="mb-3">
                            <Controller
                                name="is_contact_person"
                                control={control}
                                render={({ field }) => (
                                    <Form.Check
                                        type="checkbox"
                                        id="isContactPerson"
                                        label="Is Contact Person?"
                                        {...field}
                                        value={field.value ? 1 : 0}
                                    />
                                )}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleSubmit(onSubmit)} type="submit">
                        Save
                    </Button>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
            <ToastContainer />
        </>
    );
};

export default EmployeeFormModal;